import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEmbedJourneyFlags } from 'src/dashboard/embed/journey-flags.hook';
import { EmbedPlayerEmailGating } from 'src/dashboard/embed/player-email-gating';
import { embedUserEventsFactory } from 'src/dashboard/embed/user-events.factory';
import JourneyInactive from 'src/player/JourneyInactive';
import { Nullable } from 'src/types/nullable.type';
import {
  getAliasGateViewedForJourney,
  getEmailWithAccessForJourney,
  setAliasGateViewedForJourney,
  storeAccessedEmailForJourney,
} from 'src/utils/journeyAccess';
import { apiGetJourneyByAlias, apiSignNda, apiUnlockJourneyByToken } from 'src/utils/journeyApi';
import { getJourneySession, getJourneyTracker } from 'src/utils/tracker';
import { jDocToSections } from '../helpers/jnode/jdoc-to-sections';
import { useNeueLayoutMode } from '../helpers/neue-layout-mode.hook';
import { JNode } from '../types';
import { NeuePlayerDesktopView } from './desktop-view';
import { NeuePlayerMobileView } from './mobile-view';
import { usePlayerStore } from './player-store';
import queryString from 'query-string';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { usePlayerContext } from 'src/store/player';
import { promiseDelay } from 'src/common/helpers/promise-delay';
import { getHtmlText } from '../helpers/get-html-text';

type Props = {
  uuid: string;
  token: string;
  trackingUUID: Nullable<string>;
};

export const NeuePlayer = ({ uuid, token, trackingUUID }: Props) => {
  const [loading, setLoading] = useState(true);
  const { journey, setJourney } = usePlayerContext((state) => ({
    journey: state.journey,
    setJourney: state.setJourney,
  }));
  const initEditor = usePlayerStore((state) => state.initEditor);
  const resetEditor = usePlayerStore((state) => state.resetEditor);
  const setLayoutMode = usePlayerStore((state) => state.setLayoutMode);
  const { layoutMode, isMobileLayout } = useNeueLayoutMode();
  const layoutReady = usePlayerStore((state) => state.layout.layoutReady);

  const journeyHasBeenViewed = useRef(false);
  const [userEmail, setUserEmail] = useState<Nullable<string>>(getEmailWithAccessForJourney(uuid) || null);
  const [passwordIsVerified, setPasswordIsVerified] = useState<boolean>(false);

  const journeyTrackingUUID = useMemo(() => trackingUUID || getJourneyTracker(), [trackingUUID]);
  const journeySessionUUID = useMemo(getJourneySession, []);

  const [isGateAlreadyViewed, setIsGateAlreadyViewed] = useState<boolean>(
    getAliasGateViewedForJourney(uuid, journeySessionUUID)
  );

  const { setSuccessNotification } = useNotificationMessage();

  const {
    isJourneyInactive,
    isJourneyPasswordProtected,
    isJourneyEmailGated,
    isEmailVerificationRequired,
    isNdaSignatureRequired,
    isEmailGateUnlocked,
    checkIfNdaSignatureRequired,
    onNdaSigned,
    unlockEmailGate,
    isJourneyWhitelistEmailGated,
  } = useEmbedJourneyFlags(journey, uuid, userEmail, passwordIsVerified, false);

  const emailRequired = isJourneyEmailGated || isJourneyWhitelistEmailGated;

  const shouldRenderJourneyGate = useMemo(
    () =>
      !!journey &&
      !isGateAlreadyViewed &&
      (emailRequired || isJourneyPasswordProtected || isNdaSignatureRequired) &&
      !isEmailGateUnlocked,
    [
      journey,
      emailRequired,
      isEmailGateUnlocked,
      isGateAlreadyViewed,
      isJourneyPasswordProtected,
      isNdaSignatureRequired,
    ]
  );

  useEffect(() => {
    if (journey && userEmail && !isEmailVerificationRequired) {
      checkIfNdaSignatureRequired(userEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, isEmailVerificationRequired]);

  useEffect(() => {
    const initializeJourney = async (uuid: string) => {
      const journey = await apiGetJourneyByAlias(uuid);
      setJourney(journey);
      const jDoc: JNode = {
        type: { name: 'journey' },
        attributes: { id: '1' },
        content: (journey.steps?.[0].nodes || []).map((node: any) => {
          return {
            type: { name: 'section' },
            attributes: { id: node.uuid, name: node.name },
            content: (node?.node_blocks && JSON.parse(node.node_blocks)) || [],
          };
        }),
      };
      let sections = jDocToSections(jDoc);
      // Filter blocks that are practically empty
      sections = sections
        .map((section) => {
          return {
            ...section,
            blocks: section.blocks.filter((block) => {
              if (block.content.type === 'text') {
                return getHtmlText(block.content.value).length > 0;
              }
              if (block.content.type === 'image') {
                return !!block.content.url;
              }
              if (block.content.type === 'placeholder') {
                return false;
              }
              return true;
            }),
          };
        })
        .filter((section) => section.blocks.length > 0);
      initEditor(sections);
    };
    if (uuid) {
      Promise.all([initializeJourney(uuid), promiseDelay(3000)]).then((_) => {
        setLoading(false);
      });
    }
    return () => {
      resetEditor();
    };
  }, [uuid]);

  useLayoutEffect(() => {
    setLayoutMode(layoutMode);
  }, [layoutMode]);

  const isJourneyTrackingActive = useMemo(() => {
    // if there is no joureny, then turn off tracking
    if (!journey) return false;

    return !isJourneyInactive && !isJourneyPasswordProtected && !isJourneyEmailGated;
  }, [journey, isJourneyEmailGated, isJourneyInactive, isJourneyPasswordProtected]);

  const trackJourneyView = () => {
    if (!isJourneyTrackingActive || journeyHasBeenViewed.current) {
      return;
    }

    const journeyViewEvent = userEventsFactory.createJourneyViewEvent();
    journeyViewEvent.fire(queryString.parse(window.location.search));
    journeyHasBeenViewed.current = true;
  };

  const userEventsFactory = embedUserEventsFactory({
    aliasUUID: uuid,
    sessionUUID: journeySessionUUID,
    trackingUUID: journeyTrackingUUID,
    email: userEmail,
  });

  const onJourneyUnlocked = async (email?: string, alias_uuid: string = uuid) => {
    setAliasGateViewedForJourney(alias_uuid, journeySessionUUID);
    setIsGateAlreadyViewed(true);
    setPasswordIsVerified(true);
    unlockEmailGate();

    let response = null;

    await checkIfNdaSignatureRequired(email || '');

    if (email && journey && journey.creator?.email !== email) {
      const emailCaptureEvent = userEventsFactory.createEmailCaptureEvent(email);
      response = await emailCaptureEvent.fire();
      storeAccessedEmailForJourney(alias_uuid, email);
      trackJourneyView();
      setUserEmail(email);
      window.analytics &&
        window.analytics.track('Email Gate Passed', {
          email: email,
          journey_alias_uuid: alias_uuid,
        });
    } else {
      storeAccessedEmailForJourney(alias_uuid, 'anonymous');
      trackJourneyView();
    }
    window.history.pushState({}, document.title, window.location.pathname);

    return response;
  };

  useEffect(() => {
    // if there is a token in the url, try to unlock the journey with it.
    if (token) {
      apiUnlockJourneyByToken(token)
        .then((res) => {
          const { email, alias_uuid } = res;
          return onJourneyUnlocked(email, alias_uuid);
        })
        .catch((e) => {
          console.log(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onJourneyUnlockedPendingNda = (email?: string) => {
    return checkIfNdaSignatureRequired(email || '')
      .then((isNdaRequired: boolean) => {
        if (!isNdaRequired && journey) {
          onJourneyUnlocked(email || userEmail || '', journey.alias.uuid);
        }
      })
      .catch(unlockEmailGate);
  };

  if (journey) {
    if (isJourneyInactive) {
      return <JourneyInactive journey={journey} />;
    }

    if (shouldRenderJourneyGate) {
      const onSignNda = (email: string, signature: string) => {
        if (journey) {
          apiSignNda(journey.alias.uuid, email, signature).then(() => {
            onNdaSigned();
            // add success notification
            setSuccessNotification('Signed and email sent to you.');
            return onJourneyUnlocked(userEmail || '');
          });
        }
      };

      return (
        <EmbedPlayerEmailGating
          userEmail={userEmail}
          journey={journey}
          uuid={uuid}
          trackingUUID={journeyTrackingUUID}
          sessionUUID={journeySessionUUID}
          emailRequired={emailRequired}
          passwordRequired={isJourneyPasswordProtected}
          emailVerificationRequired={isEmailVerificationRequired}
          ndaSignatureRequired={isNdaSignatureRequired}
          onJourneyUnlocked={onJourneyUnlockedPendingNda}
          onNdaSigned={onSignNda}
          onEmailSubmitted={setUserEmail}
        />
      );
    }
  }

  return (
    <div className='w-full h-full relative'>
      <div className={classNames('w-full h-full flex transition-opacity', { 'opacity-0': loading })}>
        {isMobileLayout ? <NeuePlayerMobileView /> : <NeuePlayerDesktopView />}
      </div>
      <div
        className={classNames('absolute inset-0 flex items-center justify-center neue-text-editor transition-opacity', {
          'opacity-100': loading,
          'opacity-0 pointer-events-none': !loading,
        })}
      >
        <h1 className='animate-pulse text-center'>Loading your journey</h1>
      </div>
    </div>
  );
};
